import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import bootstrapScopedStyles from '../../styles/bootstrapScoped.min.module.css'
import customTableStyles from '../../styles/tableStyles.module.css'
import { useTable, useSortBy } from 'react-table'
import { uid } from './../../utilities/uid'
import { formatCurrency, sortCurrency } from '../../utilities/Currency'
import { formatDate, sortDate } from '../../utilities/Dates'
import onfsColors from '../../branding/colors'
import {
  useFilters,
  useGlobalFilter,
  usePagination
} from 'react-table/dist/react-table.development'

// For bootstrapTableClasses see classes applied to <table> elements here -> https://getbootstrap.com/docs/4.0/content/tables/
export default function OnfsTable({
  columns,
  data,
  bootstrapTableClasses,
  style,
  className,
  searchable = false,
  pagination = false,
  pageSizeOptions = [10, 25, 50],
  defaultSortingOptions = [],
  hiddenColumns = [],
  title,
  subtitle,
  showColumnHeaders = true,
  striping = false
}) {
  const tableName = uid()
  if (!data) return <div>No Data.</div>

  // Use appropriate sort function for table based on type
  const modifiedColumns = columns.map((col) => {
    let sortType = 'alphanumeric'

    // use custom sort function before anything else
    if (col.sortFnc) {
      sortType = col.sortFnc
    } else {
      if (col.type?.toLowerCase() === 'date') {
        sortType = sortDate
      } else if (col.type?.toLowerCase() === 'currency') {
        sortType = sortCurrency
      }
    }
    let column = { Header: col.title, accessor: col.name, sortType }
    if (col.Cell) {
      column = { ...column, Cell: col.Cell }
    }
    return column
  })

  const defaultTableClasses = [
    bootstrapScopedStyles.table,
    striping ? customTableStyles['onfs-striped'] : null,
    bootstrapScopedStyles['table-sm']
  ]
  let tableClasses = defaultTableClasses

  // Override default table classes from classes supplied by props
  if (bootstrapTableClasses) {
    tableClasses = bootstrapTableClasses.map(
      (bsClass) => bootstrapScopedStyles[bsClass]
    )
  }

  // Adding any extra classes provided by consumer
  tableClasses.push(className)

  // Format data based on type
  const modifiedData = data.map((datum) => {
    const modifiedDatum = {}
    for (let i = 0; i < modifiedColumns.length; i++) {
      const key = modifiedColumns[i].accessor

      // Use custom formatter first
      if (columns[i].formatFnc) {
        modifiedDatum[key] = columns[i].formatFnc(datum[key])
      } else {
        if (columns[i].type?.toLowerCase() === 'currency') {
          modifiedDatum[key] = formatCurrency(datum[key])
        } else if (columns[i].type?.toLowerCase() === 'date') {
          modifiedDatum[key] = formatDate(datum[key])
        } else modifiedDatum[key] = datum[key]
      }
    }
    return modifiedDatum
  })

  return (
    <Table
      tableName={tableName}
      columns={modifiedColumns}
      data={modifiedData}
      tableClasses={tableClasses}
      style={style}
      className={className}
      searchable={searchable}
      pagination={pagination}
      pageSizeOptions={pageSizeOptions}
      defaultSortingOptions={defaultSortingOptions}
      hiddenColumns={hiddenColumns}
      title={title}
      subtitle={subtitle}
      showColumnHeaders={showColumnHeaders}
    />
  )
}

function Table({
  columns,
  data,
  tableClasses,
  style,
  searchable,
  pagination,
  pageSizeOptions,
  tableName,
  defaultSortingOptions,
  hiddenColumns,
  title,
  subtitle,
  showColumnHeaders
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page, // instead of rows
    prepareRow,
    setGlobalFilter,
    preGlobalFilteredRows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      autoResetPage: false,
      autoResetExpanded: false,
      autoResetGroupBy: false,
      autoResetSelectedRows: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetRowState: false,
      initialState: {
        pageIndex: 0,
        pageSize: !pagination ? data.length : pageSizeOptions[0],
        sortBy: defaultSortingOptions,
        hiddenColumns: hiddenColumns
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const pageControlStyle = { color: onfsColors.dark, height: '100%' }
  const [searchInput, setSearchInput] = useState('')

  const handleSearchChange = (val) => {
    setSearchInput(val)
    setGlobalFilter(val)
  }

  useEffect(() => {
    handleSearchChange(searchInput)
    // If current page is out of range after data reload, go back to page 0
    if (pagination && pageIndex >= pageOptions.length) {
      gotoPage(0)
    }
  }, [data])

  return (
    <div className={bootstrapScopedStyles['bootstrap-scope']}>
      {searchable && (
        // Input element
        <div className={`${bootstrapScopedStyles['container-fluid']}`}>
          <div className={`${bootstrapScopedStyles.row}`}>
            <div
              style={{ paddingLeft: '0px' }}
              className={`${bootstrapScopedStyles['col-8']} ${bootstrapScopedStyles['col-sm-4']}`}
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                }}
              >
                <div className={bootstrapScopedStyles['form-group']}>
                  <label htmlFor={`${tableName}_global_search`} hidden>
                    Global Table Search
                  </label>
                  <input
                    aria-controls={tableName}
                    id={`${tableName}_global_search`}
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    type='search'
                    className={`${bootstrapScopedStyles['form-control']}`}
                    placeholder='Search'
                  />
                </div>
              </form>
            </div>
            {searchInput && (
              <div
                style={{ position: 'relative' }}
                className={`${bootstrapScopedStyles['font-italic']} ${bootstrapScopedStyles['col-4']} ${bootstrapScopedStyles['col-sm-8']}`}
              >
                <span
                  style={{
                    position: 'absolute',
                    padding: '10px 5px',
                    bottom: '0px',
                    right: '0px'
                  }}
                >
                  {page.length} of {preGlobalFilteredRows.length}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <table
        id={tableName}
        {...getTableProps()}
        className={tableClasses.join(' ')}
        style={style}
      >
        <thead
          ref={(el) => {
            if (el) {
              el.style.setProperty('background-color', onfsColors.primaryDark)
              el.style.setProperty('color', onfsColors.light)
            }
          }}
        >
          {title ? (
            <tr>
              <th colSpan={columns.length}> {title} </th>
            </tr>
          ) : null}
          {subtitle ? (
            <tr>
              <th
                colSpan={columns.length}
                style={{
                  backgroundColor: onfsColors.lightNeutral,
                  color: 'black'
                }}
              >
                {' '}
                {subtitle}
              </th>
            </tr>
          ) : null}
          {showColumnHeaders
            ? headerGroups.map((headerGroup, index) => (
                <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) => {
                    return (
                      // Add the sorting props to control sorting. For this example
                      // we can add them into the header props
                      <th
                        key={index}
                        style={{ verticalAlign: 'middle' }}
                        tabIndex={column.canSort ? '0' : '-1'}
                        onClick={() => column.toggleSortBy()}
                        onKeyDown={(x) => {
                          if (['Space', 'Enter'].indexOf(x.key) !== -1) {
                            column.toggleSortBy()
                          }
                        }}
                        aria-label={
                          !column.canSort
                            ? column.render('Header')
                            : 'Toggle sort by ' + column.render('Header')
                        }
                        ref={(el) => {
                          if (el) {
                            el.style.setProperty('color', 'black')
                            el.style.setProperty(
                              'background-color',
                              onfsColors.lightNeutral
                            )
                          }
                        }}
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        {column.render('Header')}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? ' ↓'
                              : ' ↑'
                            : ' ↕'}
                        </span>
                      </th>
                    )
                  })}
                </tr>
              ))
            : null}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <tr key={i} {...row.getRowProps()}>
                {row.cells.map((cell, i) => {
                  return (
                    <td key={i} {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      {pagination && (
        <nav
          aria-controls={tableName}
          role='navigation'
          aria-label='Pagination Navigation'
        >
          <ul className={bootstrapScopedStyles.pagination}>
            <li
              className={bootstrapScopedStyles['page-item']}
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              <button
                style={pageControlStyle}
                className={bootstrapScopedStyles['page-link']}
                aria-label='Go to First Page'
              >
                First
              </button>
            </li>
            <li
              className={bootstrapScopedStyles['page-item']}
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              <button
                style={pageControlStyle}
                className={bootstrapScopedStyles['page-link']}
                aria-label='Go to Previous'
              >
                {'<'}
              </button>
            </li>
            <li
              className={bootstrapScopedStyles['page-item']}
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              <button
                style={pageControlStyle}
                className={bootstrapScopedStyles['page-link']}
                aria-label='Go to Next Page'
              >
                {'>'}
              </button>
            </li>
            <li
              className={bootstrapScopedStyles['page-item']}
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              <button
                style={pageControlStyle}
                className={bootstrapScopedStyles['page-link']}
                aria-label='Go to Last Page'
              >
                Last
              </button>
            </li>
            <li
              className={`${bootstrapScopedStyles['page-item']} ${bootstrapScopedStyles.disabled}`}
            >
              <div
                style={pageControlStyle}
                className={bootstrapScopedStyles['page-link']}
              >
                Page{' '}
                <strong>
                  {pageIndex + 1} of {pageOptions.length}
                </strong>{' '}
              </div>
            </li>
            <li>
              <label htmlFor={`${tableName}_page_select`} hidden>
                Select Page Size
              </label>
              <select
                id={`${tableName}_page_select`}
                className={bootstrapScopedStyles['form-control']}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                }}
                style={{ minWidth: '100px', height: '100%' }}
              >
                {pageSizeOptions.length > 0 &&
                  pageSizeOptions.map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
              </select>
            </li>
          </ul>
        </nav>
      )}
    </div>
  )
}

// Define the expected/accepted props for OnfsTable
OnfsTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.exact({
      name: PropTypes.string.isRequired,
      title: PropTypes.any.isRequired,
      type: PropTypes.oneOf(['date', 'currency']),
      formatFnc: PropTypes.func,
      sortFnc: PropTypes.func,
      Cell: PropTypes.func
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  bootstrapTableClasses: PropTypes.arrayOf(PropTypes.string),
  style: PropTypes.object,
  className: PropTypes.string
}
